export const ClientConsents = (json: IClientConsents): IClientConsentsModel => {
	const fromJSON = () => {
		const {
			email_consent_date,
			email_marketing_consent,
			sms_consent_date,
			sms_marketing_consent,
			newsletter_consent_date,
			newsletter_marketing_consent,
			...rest
		} = json
		return {
			emailConsentDate: email_consent_date ? new Date(email_consent_date) : null,
			emailMarketingConsent: email_marketing_consent,
			smsConsentDate: sms_consent_date ? new Date(sms_consent_date) : null,
			smsMarketingConsent: sms_marketing_consent,
			newsletterConsentDate: newsletter_consent_date ? new Date(newsletter_consent_date) : null,
			newsletterMarketingConsent: newsletter_marketing_consent,
			...rest,
		}
	}

	return { ...fromJSON() }
}

export const ClientProfile = (json: IClientProfile): IClientProfileModel => {
	const fromJSON = () => {
		const { phone_number, email_verified_at, email_verification_token, first_name, last_name, client_id, ...rest } =
			json
		return {
			clientID: client_id,
			firstName: first_name,
			lastName: last_name,
			phoneNumber: phone_number,
			emailVerifiedAt: email_verified_at ? new Date(email_verified_at) : null,
			emailVerificationToken: email_verification_token,
			...rest,
		}
	}

	return { ...fromJSON() }
}

export const clientFromJSON = (json: IClient): IClientModel => {
	const {
		first_name,
		last_name,
		created_at,
		client,
		birth_date,
		is_active_device_tokens,
		is_influencer,
		client_id,
		client_consents,
		weight_goal,
		diet_expectation,
		...rest
	} = json

	return {
		firstName: first_name,
		lastName: last_name,
		createdAt: created_at ? new Date(created_at) : null,
		client: client ? ClientProfile(client) : null,
		birthDate: birth_date ? new Date(birth_date) : null,
		clientConsents: client_consents ? ClientConsents(client_consents) : null,
		isActiveDeviceTokens: is_active_device_tokens,
		isInfluencer: is_influencer,
		clientID: client_id,
		weightGoal: weight_goal,
		dietExpectation: diet_expectation,
		...rest,
	}
}

export const clientProfileToJSON = (obj: Partial<IClientProfileModel>): Partial<IClientProfile> => {
	const { phoneNumber, ...rest } = obj
	return {
		phone_number: phoneNumber,
		...rest,
	}
}

export const clientToJSON = (obj: Partial<IClientModel>): Partial<IClient> => {
	const { firstName, lastName, client, phoneNumber, birthDate, isInfluencer, ...rest } = obj
	return {
		...rest,
		first_name: firstName,
		last_name: lastName,
		client: client ? clientProfileToJSON(client) : undefined,
		phone_number: phoneNumber || undefined,
		birth_date: birthDate ?? undefined,
		is_influencer: isInfluencer,
	}
}

export const ClientAddress = (json: IClientAddress): IClientAddressModel => {
	const {
		is_assigned,
		is_visible,
		building_type,
		building_number,
		apartment_number,
		entry_code,
		delivery_until,
		delivery_place,
		courier_instructions,
		updated_at,
		created_at,
		delivery_phone_number,
		is_alternative_delivery_phone_number,
		...rest
	} = json

	return {
		...rest,
		isAssigned: is_assigned,
		isVisible: is_visible,
		buildingType: building_type,
		buildingNumber: building_number,
		apartmentNumber: apartment_number,
		deliveryUntil: delivery_until,
		deliveryPlace: delivery_place,
		courierInstructions: courier_instructions,
		entryCode: entry_code,
		deliveryPhoneNumber: delivery_phone_number,
		isAlternativeDeliveryPhoneNumber: is_alternative_delivery_phone_number,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const ClientAddressFormToJson = (obj: IClientAddressFormModel): IClientAddressFormJson => {
	const {
		apartmentNumber,
		buildingNumber,
		buildingType,
		courierInstructions,
		deliveryPlace,
		deliveryUntil,
		entryCode,
		deliveryPhoneNumber,
		isAlternativeDeliveryPhoneNumber,
		...rest
	} = obj

	return {
		...rest,
		apartment_number: apartmentNumber,
		building_number: buildingNumber,
		building_type: buildingType,
		courier_instructions: courierInstructions,
		delivery_place: deliveryPlace,
		delivery_until: deliveryUntil,
		entry_code: entryCode,
		delivery_phone_number: deliveryPhoneNumber ? deliveryPhoneNumber.split(' ').join('') : null,
		is_alternative_delivery_phone_number: isAlternativeDeliveryPhoneNumber,
	}
}

export const ClientAddressManageToJson = (obj: IClientAddressCreateModel): IClientAddressCreateJson => {
	const { clientUUID, ...rest } = obj

	return {
		...ClientAddressFormToJson(rest),
		client_uuid: clientUUID,
	}
}

export const ClientRating = (json: IClientRating): IClientRatingModel => {
	const { ...rest } = json

	return {
		...toCamelCase(rest),
	}
}

export const ClientOrderRating = (json: IClientOrderRating): IClientOrderRatingModel => {
	const { ...rest } = json

	return {
		...toCamelCase(rest),
	}
}

export const walletStatsToJSON = (json: IWalletStats): IWalletStatsModel => {
	const { current_balance, pending_cashback, current_cashback_percentage } = json
	return {
		currentBalance: current_balance,
		pendingCashback: pending_cashback,
		currentCashbackPercentage: current_cashback_percentage,
	}
}

const walletOverviewEntryToJSON = (entry: IWalletOverviewEntry): IWalletOverviewEntryModel => {
	const { state } = entry

	return {
		available: state.available,
		bookBalance: state.book_balance,
		holds: state.holds,
	}
}

export const walletOverviewToJSON = (json: IWalletOverview): IWalletOverviewModel => {
	const { wallets, total_available } = json

	return {
		topUp: walletOverviewEntryToJSON(wallets.top_up),
		cashback: walletOverviewEntryToJSON(wallets.cashback),
		promo: walletOverviewEntryToJSON(wallets.promo),
		totalAvailable: total_available,
	}
}

const clientProgram800WalletFromJSON = (json: IClientProgram800Wallet): IClientProgram800WalletModel => {
	const { book_balance, holds, available } = json

	return {
		bookBalance: book_balance,
		holds,
		available,
	}
}

const clientProgram800EnrollmentFromJSON = (json: IClientProgram800Enrollment): IClientProgram800EnrollmentModel => {
	const {
		uuid,
		status,
		enrolled_at,
		suspended_at,
		last_verified_at,
		consent_marketing_email,
		consent_marketing_automation,
		consent_marketing_phone_number,
		consent_regulations,
		wallet,
	} = json

	return {
		uuid,
		status,
		enrolledAt: enrolled_at ? new Date(enrolled_at) : null,
		suspendedAt: suspended_at ? new Date(suspended_at) : null,
		lastVerifiedAt: last_verified_at ? new Date(last_verified_at) : null,
		consentMarketingEmail: consent_marketing_email,
		consentMarketingAutomation: consent_marketing_automation,
		consentMarketingPhoneNumber: consent_marketing_phone_number,
		consentRegulations: consent_regulations,
		wallet: wallet ? clientProgram800WalletFromJSON(wallet) : null,
	}
}

export const clientProgram800StatusFromJSON = (json: IClientProgram800ApiResponse): IClientProgram800StatusModel => {
	const { data } = json
	const enrollment = 'status' in data ? clientProgram800EnrollmentFromJSON(data) : null

	return {
		enrollment,
		wallet: enrollment?.wallet ?? null,
	}
}

export const referrakStatsToJSON = (json: IReferralStats): IReferralStatsModel => {
	const { pending_earnings, referred_count, total_cashback_earned, ...rest } = json

	return {
		...rest,
		pendingEarnings: pending_earnings,
		referredCount: referred_count,
		totalCashbackEarned: total_cashback_earned,
	}
}

export const clientWalletHistoryToJSON = (json: IClientWalletHistory): IClientWalletHistoryModel => {
	const { wallet_id, wallet_type, idempotency_key, metadata, created_at, updated_at, book_balance_after, ...rest } =
		json

	return {
		...rest,
		walletId: wallet_id,
		walletType: wallet_type,
		idempotencyKey: idempotency_key,
		bookBalanceAfter: book_balance_after,
		metadata: metadata
			? {
					action: metadata.action,
					triggeredBy: metadata.triggered_by,
					employeeId: metadata.employee_id,
				}
			: null,
		createdAt: created_at,
		updatedAt: updated_at,
	}
}
